/**
 * Variant media grouping.
 *
 * Shopify allows only one featured image per variant, so galleries that need
 * several photos per color rely on alt-text tags instead: an image whose alt
 * text ends with "#ColorName" belongs to that color, and untagged media is
 * shared by every color.
 *
 * All product media stays in the DOM. Switching color only toggles visibility
 * and refreshes the existing sliders in place, so the gallery layout (thumbs
 * position, Swiper direction, heights) is never rebuilt.
 */
(function () {
  const ROOT_SELECTOR = '[data-alt-media-grouping="true"]';
  const ITEM_SELECTOR = "[data-media-group]";
  const SLIDE_CLASS = "swiper-slide";
  const HIDDEN_ITEM_CLASS = "product__media-item--variant-hidden";
  const HIDDEN_SUBITEM_CLASS = "product__media-subitem--variant-hidden";
  const LAST_VISIBLE_CLASS = "product__media-item--variant-last-visible";
  const HIDDEN_THUMBS_CLASS = "product__media-sublist--variant-hidden";

  /**
   * Mirrors the Liquid `handle` filter so option values and alt-text tags are
   * compared the same way on the server and on the client.
   */
  function handleize(value) {
    return String(value == null ? "" : value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function getRoots(sectionId) {
    return Array.prototype.filter.call(
      document.querySelectorAll(ROOT_SELECTOR),
      (root) => root.dataset.mediaGroupSection === String(sectionId)
    );
  }

  function isSubItem(item) {
    return item.hasAttribute("data-media-sub-id");
  }

  function hiddenClassFor(item) {
    return isSubItem(item) ? HIDDEN_SUBITEM_CLASS : HIDDEN_ITEM_CLASS;
  }

  function isHidden(item) {
    return item.classList.contains(hiddenClassFor(item));
  }

  function getGroupValue(root, variant) {
    const position = parseInt(root.dataset.mediaGroupPosition, 10);

    if (!position || position < 1 || !variant || !variant.options) {
      return root.dataset.mediaGroupSelected || "";
    }

    return handleize(variant.options[position - 1]);
  }

  /**
   * Moves hidden media to the end of its container so that layouts positioned
   * with :nth-child (and the trailing divider) keep working on the visible set.
   */
  function compact(parent) {
    const items = Array.prototype.filter.call(parent.children, (child) =>
      child.hasAttribute("data-media-group")
    );

    if (!items.length) return;

    items.forEach((item) => item.classList.remove(LAST_VISIBLE_CLASS));
    items.filter(isHidden).forEach((item) => parent.appendChild(item));

    const visible = items.filter((item) => !isHidden(item));
    if (visible.length) {
      visible[visible.length - 1].classList.add(LAST_VISIBLE_CLASS);
    }
  }

  function applyVisibility(root, groupValue) {
    const parents = [];

    root.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      const group = item.dataset.mediaGroup;
      const visible = !group || !groupValue || group === groupValue;

      item.classList.toggle(hiddenClassFor(item), !visible);

      const parent = item.parentElement;
      if (!parent) return;

      // Hidden media must stay in the wrapper without being a Swiper slide.
      if (parent.classList.contains("swiper-wrapper")) {
        item.classList.toggle(SLIDE_CLASS, visible);
      }

      if (parents.indexOf(parent) === -1) parents.push(parent);
    });

    parents.forEach(compact);
  }

  function refreshSwiper(swiper) {
    if (!swiper || swiper.destroyed) return;

    const wasVertical = swiper.isVertical();

    try {
      swiper.updateSize();
      swiper.updateSlides();
      swiper.updateProgress();
      swiper.updateSlidesClasses();
      swiper.update();
    } catch (error) {
      return;
    }

    // Never let a refresh flip the direction the gallery was rendered with.
    if (swiper.isVertical() !== wasVertical) {
      swiper.changeDirection(wasVertical ? "vertical" : "horizontal");
    }
  }

  function findSlideIndex(swiper, mediaId) {
    if (!mediaId) return -1;

    return Array.prototype.findIndex.call(
      swiper.slides,
      (slide) => slide.dataset && slide.dataset.mediaId === mediaId
    );
  }

  function refreshSliders(root, targetMediaId) {
    root.querySelectorAll(".js-media-list").forEach((mainElement) => {
      const scope =
        mainElement.closest(".product__media-list-wrapper") ||
        mainElement.parentElement ||
        root;
      const thumbsElement = scope.querySelector(".js-media-sublist");
      const mainSwiper = mainElement.swiper;
      const thumbsSwiper = thumbsElement ? thumbsElement.swiper : null;

      // A single thumb carries no information — and the strip has to be shown
      // again before Swiper can measure it.
      if (thumbsElement) {
        const visibleThumbs = scope.querySelectorAll(
          `.product__media-subitem.${SLIDE_CLASS}`
        ).length;

        thumbsElement.classList.toggle(HIDDEN_THUMBS_CLASS, visibleThumbs <= 1);
      }

      // Thumbs first, so the main slider picks up the new thumb slides.
      refreshSwiper(thumbsSwiper);
      refreshSwiper(mainSwiper);

      if (targetMediaId && mainSwiper && !mainSwiper.destroyed) {
        const index = findSlideIndex(mainSwiper, targetMediaId);
        const slideIndex = index === -1 ? 0 : index;

        mainSwiper.slideTo(slideIndex, 0);
        if (thumbsSwiper && !thumbsSwiper.destroyed) {
          thumbsSwiper.slideTo(slideIndex, 0);
        }
      }
    });

    if (typeof window.updatethumbnail === "function") {
      window.updatethumbnail();
    }
  }

  /**
   * Filters the gallery of a section down to the media group of `variant`.
   *
   * @param {string} sectionId - Section id the gallery belongs to
   * @param {object} variant - Currently selected variant
   */
  function updateVariantMediaDisplay(sectionId, variant) {
    getRoots(sectionId).forEach((root) => {
      const groupValue = getGroupValue(root, variant);

      root.dataset.mediaGroupSelected = groupValue;
      applyVisibility(root, groupValue);

      const targetMediaId =
        variant && variant.featured_media
          ? `${sectionId}-${variant.featured_media.id}`
          : null;

      refreshSliders(root, targetMediaId);
    });
  }

  /**
   * Syncs the gallery with the group Liquid already rendered. Liquid filters the
   * first paint, so this only settles what depends on the visible count.
   */
  function init(scope) {
    const container =
      scope && typeof scope.querySelectorAll === "function" ? scope : document;

    container.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      applyVisibility(root, root.dataset.mediaGroupSelected || "");
      refreshSliders(root, null);
    });
  }

  window.updateVariantMediaDisplay = updateVariantMediaDisplay;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target || document);
  });
})();
